import BoxMath from "./boxMath";
import {
	BoundingBox,
	SnappingRegions,
	BoxCoordinates,
	Vertex,
	XYCoordinates,
	MoveRequest,
	Edges,
	SharedEdges,
	SharedCorners,
} from "./types";

class DockableBox {
	left!: number;

	top!: number;

	right!: number;

	bottom!: number;

	width!: number;

	height!: number;

	windowBoundingBox!: BoundingBox;

	getVertices: (req: BoxCoordinates) => Vertex[];

	buffer!: BoundingBox;

	innerBuffer!: BoundingBox;

	monitor?: BoxCoordinates;

	resizeHandle: string | null = null;

	snappingRegions!: SnappingRegions;

	bufferSize!: number;

	constructor(bounds?: BoxCoordinates) {
		if (bounds) {
			this.left = bounds.left;
			this.top = bounds.top;
			this.right = bounds.right;
			this.bottom = bounds.bottom;
			this.width = bounds.right - bounds.left;
			this.height = bounds.bottom - bounds.top;
			this.windowBoundingBox = BoxMath.getWindowBoundingBox(this.getBounds());
		}
		this.setSnappingRegions();
		this.getVertices = BoxMath.getVertices;
		return this;
	}

	/**
	 * @function {function name}
	 * @param  {type} format         {description}
	 * @param  {type} includeCorners {description}
	 * @return {type} {description}
	 */
	getEdges(format: string, includeCorners?: number | boolean): Edges | BoundingBox[] {
		if (includeCorners === undefined || includeCorners === true) {
			includeCorners = 0;
		} else {
			includeCorners = 1;
		}

		const top = {
			min: {
				x: this.left + includeCorners,
				y: this.top,
			},
			max: {
				x: this.right - includeCorners,
				y: this.top,
			},
		};
		const left = {
			min: {
				x: this.left,
				y: this.top + includeCorners,
			},
			max: {
				x: this.left,
				y: this.bottom - includeCorners,
			},
		};
		const right = {
			min: {
				x: this.right,
				y: this.top + includeCorners,
			},
			max: {
				x: this.right,
				y: this.bottom - includeCorners,
			},
		};
		const bottom = {
			min: {
				x: this.left + includeCorners,
				y: this.bottom,
			},
			max: {
				x: this.right - includeCorners,
				y: this.bottom,
			},
		};

		if (format === "obj") {
			return {
				top,
				right,
				bottom,
				left,
			};
		}
		return [top, right, bottom, left];
	}

	getCorners(): Record<string, Vertex> {
		return this.getCornerObject(this.getBounds());
	}

	/**
	 * @function {function name}
	 * @param  {type} point          {description}
	 * @param  {type} includeCorners {description}
	 * @return {type} {description}
	 */
	pointIsOnBoundingBox(point: XYCoordinates, includeCorners: boolean = true): boolean {
		// If it's on the top or bottom edge.
		const edges = <BoundingBox[]>this.getEdges("arr", includeCorners);

		for (let i = 0; i < edges.length; i++) {
			const segment = edges[i];
			if (BoxMath.isPointOnSegment(point, segment)) {
				return true;
			}
		}
		return false;
	}

	/**
	 * @function {function name}
	 * @param  {type} corner {description}
	 * @return {type} {description}
	 */
	getPointByVertex(corner: string): XYCoordinates {
		corner = corner.toLowerCase();
		const point = {
			x: this.left,
			y: this.top,
		};
		if (corner.includes("bottom")) {
			point.y = this.bottom;
		}
		if (corner.includes("right")) {
			point.x = this.right;
		}
		return point;
	}

	/**
	 * @function {function name}
	 * @param  {type} point     {description}
	 * @return {type} {description}
	 */
	getEdgeByPoint(point: XYCoordinates): string {
		const edges = this.getEdges("obj");

		for (const edge in edges) {
			if (BoxMath.isPointOnSegment(point, edges[edge])) {
				return edge;
			}
		}
		// Should never get here
		return edges[0];
	}

	/**
	 * @function {function name}
	 * @param  {type} point     {description}
	 * @param  {type} tolerance {description}
	 * @return {type} {description}
	 */
	getVertexByPoint(point: XYCoordinates, tolerance?: number): string | null {
		if (tolerance === undefined) {
			tolerance = 0;
		}
		let corner: string | null = null;
		let justAnEdge = true;
		if (point.y <= this.windowBoundingBox.max.y + tolerance && point.y >= this.windowBoundingBox.max.y - tolerance) {
			corner = "bottom";
		}
		if (point.y <= this.windowBoundingBox.min.y + tolerance && point.y >= this.windowBoundingBox.min.y - tolerance) {
			corner = "top";
		}

		if (!corner) {
			return corner;
		}

		if (point.x <= this.windowBoundingBox.min.x + tolerance && point.x >= this.windowBoundingBox.min.x - tolerance) {
			justAnEdge = false;
			corner = `${corner}Left`;
		}

		if (point.x <= this.windowBoundingBox.max.x + tolerance && point.x >= this.windowBoundingBox.max.x - tolerance) {
			justAnEdge = false;
			corner = `${corner}Right`;
		}
		if (justAnEdge) {
			return null;
		}
		return corner;
	}

	/**
	 * @function getSharedEdges Determines if another window has edges to which our window may snap/dock
	 * @param  {type} win2 {description}		* @param  {Window} win2 Window proxy (movement object) of other window to which our window may snap/dock
	 * @return {type} {description}		* @param  {number} tolerance Buffer region around edges to determine region for snap / dock
	 * @return {object} Object describing shared edges, with boolean top, bottom, left, and right properties
	 */
	getSharedEdges(win2: BoxCoordinates, tolerance: number = 0): SharedEdges {
		// from perspective of stationary window;
		const sharedEdges = {
			top: false,
			left: false,
			right: false,
			bottom: false,
		};
		// +--------------------------------+
		// |    Monitor2                    |
		// |                                |
		// |           +---------+          |
		// |           |         |          |
		// |           |  Win2   |          |
		// |           |         |          |
		// +--------------------------------+
		// |           |         |          |
		// |           | MyWindow|          |
		// |           |         |          |
		// |           +---------+          |
		// |    Monitor1                    |
		// |                                |
		// +--------------------------------+
		// Return all false if we aren't intersecting at all or snapping exactly across the monitor boundary,
		// e.g. (see above) if top of my window is snapped to bottom of the other window and the top of my other window is the top
		// of the monitor, return shared edges.
		const intersection = BoxMath.intersectBoundingBoxes(this.buffer, <BoundingBox>win2.windowBoundingBox);
		if (
			!intersection ||
			(this.monitor &&
				((this.top === win2.bottom && this.top === this.monitor.top) ||
					(this.left === win2.right && this.left === this.monitor.left) ||
					(this.bottom === win2.top && this.bottom === this.monitor.bottom) ||
					(this.right === win2.left && this.right === this.monitor.right)))
		) {
			return sharedEdges;
		}
		const inRightTolerance = BoxMath.between({
			num: win2.right,
			min: this.left - tolerance,
			max: this.left + tolerance,
			inclusive: true,
		});
		const inLeftTolerance = BoxMath.between({
			num: win2.left,
			min: this.right - tolerance,
			max: this.right + tolerance,
			inclusive: true,
		});
		const inTopTolerance = BoxMath.between({
			num: win2.top,
			min: this.bottom - tolerance,
			max: this.bottom + tolerance,
			inclusive: true,
		});
		const inBottomTolerance = BoxMath.between({
			num: win2.bottom,
			min: this.top - tolerance,
			max: this.top + tolerance,
			inclusive: true,
		});

		if (inRightTolerance && win2.bottom > this.top - tolerance && win2.top < this.bottom + tolerance) {
			sharedEdges.left = true;
		}
		// if(600 > 0 && 300 < 300)
		if (inLeftTolerance && win2.bottom > this.top - tolerance && win2.top < this.bottom + tolerance) {
			sharedEdges.right = true;
		}

		if (inBottomTolerance && win2.left < this.right + tolerance && win2.right > this.left - tolerance) {
			sharedEdges.top = true;
		}

		if (inTopTolerance && win2.left < this.right + tolerance && win2.right > this.left - tolerance) {
			sharedEdges.bottom = true;
		}

		return sharedEdges;
	}

	/**
	 * @function getSharedCorners Determines if another window has corners to which our window may snap/dock
	 * @param  {type} win2 {description}		* @param  {Window} win2 Window proxy (movement object) of other window to which our window may snap/dock
	 * @return {type} {description}		* @return {object} Object describing shared corners, including original vertices (topLeft, topRight, bottomRight, bottomLeft),
	 *					and amended vertices (rightTop, rightBottom, leftTop, leftBottom) to indicate shared tops or bottoms on
	 *					on the left or right.
	 */
	getSharedCorners(win2: BoxCoordinates): SharedCorners {
		const sharedCorners = {
			// Matched corners
			topLeft: false,
			topRight: false,
			bottomLeft: false,
			bottomRight: false,

			// Adjoined corners
			rightTop: false,
			rightBottom: false,
			leftTop: false,
			leftBottom: false,
		};

		// Are we intersecting at all?
		const intersection =
			win2.windowBoundingBox &&
			!BoxMath.intersectBoundingBoxes(this.innerBuffer, win2.windowBoundingBox) &&
			BoxMath.intersectBoundingBoxes(this.buffer, win2.windowBoundingBox);
		// If not, then return all corners false
		if (!intersection) return sharedCorners;

		// Are we snapping exactly on a monitor boundary?
		// +--------------------------------+
		// |    Monitor2                    |
		// |                                |
		// |           +---------+          |
		// |           |         |          |
		// |           |  Win2   |          |
		// |           |         |          |
		// +--------------------------------+
		// |           |         |          |
		// |           | MyWindow|          |
		// |           |         |          |
		// |           +---------+          |
		// |    Monitor1                    |
		// |                                |
		// +--------------------------------+
		// e.g. (see above) if top of my window is snapped to bottom of the other window and the top of my other window is the top
		// of the monitor
		const onTopMonitorBoundary = this.top === win2.bottom && this.top === this.monitor?.top;
		const onLeftMonitorBoudnary = this.left === win2.right && this.left === this.monitor?.left;
		const onBottomMonitorBoundary = this.bottom === win2.top && this.bottom === this.monitor?.bottom;
		const onRightMonitorBoundary = this.right === win2.left && this.right === this.monitor?.right;
		const onAnyMonitorBoundary =
			onTopMonitorBoundary || onLeftMonitorBoudnary || onBottomMonitorBoundary || onRightMonitorBoundary;
		// If so, return all corners false because the window will be snapped to the monitor boundary.
		if (onAnyMonitorBoundary) return sharedCorners;

		const isSameCorner = (c1: Vertex, c2: Vertex): boolean => c1.x === c2.x && c1.y === c2.y;

		// Set any matched corners between the two windows to true
		const myCorners = BoxMath.getVertices(this.getBounds());
		const theirCorners = BoxMath.getVertices(win2);
		myCorners.forEach((ourCorner) => {
			theirCorners.forEach((theirCorner) => {
				sharedCorners[ourCorner.label] = isSameCorner(ourCorner, theirCorner);
			});
		});

		// Win1's X1,Y0 is adjoined to Win2's X0,Y0
		// +---------------------------+
		// |                           |
		// |   +--------+---------+    |
		// |   |        |         |    |
		// |   |  Win1  |  Win2   |    |
		// |   |        |         |    |
		// +---------------------------+

		const right = this.right === win2.left || this.right === win2.right; // second half of this conditional doesn't seem correct
		const left = this.left === win2.right || this.left === win2.left; // second half of this conditional doesn't seem correct
		const sharedTop = this.top === win2.top;
		const sharedBottom = this.bottom === win2.bottom;

		return {
			...sharedCorners,
			rightTop: right && sharedTop,
			rightBottom: right && sharedBottom,
			leftBottom: left && sharedBottom,
			leftTop: left && sharedTop,
		};
	}

	/**
	 * @function {function name}
	 * @return {type} {description}
	 */
	getBounds(): BoxCoordinates {
		return {
			left: this.left,
			right: this.right,
			top: this.top,
			bottom: this.bottom,
			width: this.width,
			height: this.height,
		};
	}

	/**
	 * @function {function name}
	 * @param  {type} req {description}
	 * @return {type} {description}
	 */
	getCornerObject(req): Record<string, Vertex> {
		const corners = {};
		const vertices = BoxMath.getVertices(req);
		for (let i = 0; i < vertices.length; i++) {
			const corner = vertices[i];
			corners[corner.label] = corner;
		}
		return corners;
	}

	/**
	 * @function {function name}
	 * @param  {type} mouse {description}
	 * @return {type} {description}
	 */
	getGrabbedEdge(mouse: XYCoordinates): string | boolean {
		const tolerance = 15;
		const mouseBox = {
			min: {
				x: mouse.x - tolerance,
				y: mouse.y - tolerance,
			},
			max: {
				x: mouse.x + tolerance,
				y: mouse.y + tolerance,
			},
		};
		const edges = ["top", "bottom", "left", "right"];
		for (let i = 0; i < edges.length; i++) {
			const edge = edges[i];
			if (BoxMath.intersectBoundingBoxes(mouseBox, this.snappingRegions[edge])) {
				return edge;
			}
		}
		return false;
	}

	computeResizeHandleFromMove(request: MoveRequest): string | null {
		if (request.top !== this.top) {
			if (request.right !== this.right) {
				return "topRight";
			} else if (request.left !== this.left) {
				return "topLeft";
			}
			return "top";
		} else if (request.right !== this.right) {
			if (request.bottom !== this.bottom) {
				return "bottomRight";
			} else if (request.top !== this.top) {
				return "topRight";
			}
			return "right";
		} else if (request.bottom !== this.bottom) {
			if (request.left !== this.left) {
				return "bottomLeft";
			} else if (request.right !== this.right) {
				return "bottomRight";
			}
			return "bottom";
		} else if (request.left !== this.left) {
			if (request.top !== this.top) {
				return "topLeft";
			} else if (request.bottom !== this.bottom) {
				return "bottomLeft";
			}
			return "left";
		}
		return null;
	}

	/**
	 * @function {function name}
	 * @param  {type} request {description}
	 * @return {type} {description}
	 */
	getResizeHandle(request: MoveRequest): string {
		if (this.resizeHandle) return this.resizeHandle;

		let resizeHandle;
		if (request.mousePosition) {
			resizeHandle = this.getVertexByPoint(request.mousePosition, 15) ?? this.getGrabbedEdge(request.mousePosition);
		}

		resizeHandle = resizeHandle ?? this.computeResizeHandleFromMove(request);

		if (resizeHandle && request.changeType !== 0) {
			// if we didn't find a resizeHandle, then no edges moved. Send last handle.
			this.resizeHandle = resizeHandle;
		} else if (!resizeHandle && request.changeType === 0) {
			resizeHandle = "top";
		}
		return resizeHandle;
	}

	/**
	 * @function {function name}
	 * @param  {type} bufferSize {description}
	 * @return {type} {description}
	 */
	setBuffer(bufferSize?: number) {
		if (bufferSize === undefined && this.bufferSize === null) {
			return;
		}
		if (bufferSize !== undefined) {
			this.bufferSize = bufferSize;
		} else {
			({ bufferSize } = this);
		}

		this.buffer = {
			min: {
				x: this.left - bufferSize,
				y: this.top - bufferSize,
			},
			max: {
				x: this.right + bufferSize,
				y: this.bottom + bufferSize,
			},
		};
	}

	/**
	 * @function {function name}
	 * @return {type} {description}
	 */
	setSnappingRegions() {
		this.snappingRegions = BoxMath.getSnappingRegions(this, this.bufferSize);
	}

	sharesACornerWith(win: BoxCoordinates): boolean {
		const sharedCorners = this.getSharedCorners(win);
		const corners = [
			"topLeft",
			"topRight",
			"rightTop",
			"leftTop",
			"bottomRight",
			"bottomLeft",
			"rightBottom",
			"leftBottom",
		];
		for (let i = 0; i < corners.length; i++) {
			const corner = corners[i];
			if (sharedCorners[corner]) {
				return true;
			}
		}
		return false;
	}

	/**
	 * @function {function name}
	 * @param  {type} win {description}
	 * @return {type} {description}
	 */
	sharesAnEdgeWith(win: BoxCoordinates): boolean {
		const sharedEdges = this.getSharedEdges(win);

		const edges = ["top", "right", "left", "bottom"];
		for (let i = 0; i < edges.length; i++) {
			const edge = edges[i];
			if (sharedEdges[edge]) {
				return true;
			}
		}
		return false;
	}

	/**
	 * @function {function name}
	 * @param  {type} request {description}
	 * @return {type} {description}
	 */
	canSnapToWindow(request: MoveRequest): boolean {
		if (this.sharesAnEdgeWith(request)) {
			return true;
		}
		return (
			BoxMath.intersectBoundingBoxes(this.buffer, request.windowBoundingBox) &&
			!BoxMath.intersectBoundingBoxes(this.innerBuffer, request.windowBoundingBox)
		);
	}
}
export default DockableBox;
